import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { Toast } from 'primeng/toast';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, of } from 'rxjs';

import { IglesiasService } from '../../core/services/iglesias.service';
import { UserStore } from '../../core/services/user-store.service';
import { SolicitudesService } from '../../core/services/solicitudes.service';
import { UsersService } from '../../core/services/users.service';
import { AvisoActivarPushComponent } from '../../shared/components/aviso-activar-push/aviso-activar-push.component';
import { ContentCardComponent } from '../../shared/components/content-card/content-card.component';
import {
  PersonTableActionEvent,
  PersonTableComponent,
  PersonTablePageChange,
} from '../../shared/components/person-table/person-table.component';
import { CongregacionFiltro } from '../../shared/enums/congregacion-filtro.enum';
import { buildCongregacionColumns } from '../../shared/helpers/build-congregacion-columns';
import { BUSQUEDA_NOMBRE_DELAY_MS } from '../../shared/helpers/busqueda-nombre-delay';
import { congregacionFiltroCopy } from '../../shared/helpers/congregacion-filtro-copy';
import { mapUserToPersonTableRow } from '../../shared/helpers/map-congregacion-row';
import { PersonTableColumn, PersonTableRow } from '../../shared/interfaces/person-table.interface';
import { UserListItem } from '../../shared/interfaces/user.interface';

type AccionDiscipulos = 'transferir' | 'sin-lider';

export interface FiltroOption {
  id: CongregacionFiltro;
  label: string;
}

const PAGE_SIZE = 12;

@Component({
  selector: 'app-congregacion',
  standalone: true,
  imports: [
    FormsModule,
    IconField,
    InputIcon,
    InputText,
    AvisoActivarPushComponent,
    ContentCardComponent,
    PersonTableComponent,
    Toast,
    ConfirmDialog,
    Dialog,
    Button,
    Select,
  ],
  templateUrl: './congregacion.component.html',
})
export class CongregacionComponent implements OnInit, OnDestroy {
  private readonly usersService = inject(UsersService);
  private readonly solicitudesService = inject(SolicitudesService);
  private readonly iglesiasService = inject(IglesiasService);
  private readonly userStore = inject(UserStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmation = inject(ConfirmationService);
  private readonly messages = inject(MessageService);

  private loadUsersSub?: Subscription;
  private loadIglesiaSub?: Subscription;
  private readonly search$ = new Subject<string>();
  private searchSub?: Subscription;

  readonly CongregacionFiltro = CongregacionFiltro;
  readonly pageSize = PAGE_SIZE;

  readonly filtro = signal<CongregacionFiltro>(CongregacionFiltro.Ninguno);
  readonly cantCulto = signal(1);
  readonly search = signal('');
  readonly esperandoBusqueda = signal(false);
  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly allUsers = signal<UserListItem[]>([]);
  readonly page = signal(1);

  readonly avisoLiderVisible = signal(false);
  readonly elegirLiderVisible = signal(false);
  readonly discipulosTotal = signal(0);
  readonly lideresOpciones = signal<{ label: string; value: string }[]>([]);
  readonly liderDestino = signal<string | null>(null);
  readonly cargandoLideres = signal(false);
  readonly personaAEliminar = signal<PersonTableRow | null>(null);
  readonly totalRecords = signal(0);
  readonly yoId = computed(() => this.userStore.user()?._id ?? null);
  readonly textoDiscipulos = computed(() => {
    const total = this.discipulosTotal();
    if (total === 1) {
      return 'tiene 1 persona a cargo';
    }
    if (total > 1) {
      return `tiene ${total} personas a cargo`;
    }
    return 'tiene personas a cargo';
  });
  readonly nombreAEliminar = computed(
    () => this.personaAEliminar()?.nombreCompleto || 'Esta persona',
  );
  readonly isAdmin = computed(() => this.userStore.isAdmin());

  readonly filtroOptions: FiltroOption[] = [
    { id: CongregacionFiltro.Ninguno, label: 'Sin filtro' },
    { id: CongregacionFiltro.PorNombre, label: 'Por nombre' },
    { id: CongregacionFiltro.Lideres, label: 'Líderes' },
    { id: CongregacionFiltro.Monitoreo, label: 'Monitoreo' },
    { id: CongregacionFiltro.Faltas, label: 'Lista Roja' },
    { id: CongregacionFiltro.Cumple, label: 'Cumpleaños' },
    { id: CongregacionFiltro.Muelle, label: 'Muelle' },
    { id: CongregacionFiltro.Estudiantes, label: 'Estudiantes/Bautismos' },
  ];

  readonly filtroCopy = computed(() => congregacionFiltroCopy(this.filtro()));
  readonly showNameSearch = computed(() => this.filtro() === CongregacionFiltro.PorNombre);
  readonly first = computed(() => (this.page() - 1) * PAGE_SIZE);

  readonly columns = computed<PersonTableColumn[]>(() =>
    buildCongregacionColumns(this.filtro(), this.cantCulto()),
  );

  readonly rows = computed<PersonTableRow[]>(() => this.allUsers().map(mapUserToPersonTableRow));

  ngOnInit(): void {
    this.aplicarFiltroDeUrl();
    this.loadIglesia();
    this.loadUsers();

    this.searchSub = this.search$
      .pipe(debounceTime(BUSQUEDA_NOMBRE_DELAY_MS), distinctUntilChanged())
      .subscribe((value) => {
        this.esperandoBusqueda.set(false);
        this.search.set(value);
        if (this.filtro() === CongregacionFiltro.PorNombre) {
          this.page.set(1);
          this.loadUsers();
        }
      });
  }

  ngOnDestroy(): void {
    this.loadUsersSub?.unsubscribe();
    this.loadIglesiaSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }

  onFiltroChange(value: string): void {
    this.esperandoBusqueda.set(false);
    this.filtro.set(value as CongregacionFiltro);
    if (value !== CongregacionFiltro.PorNombre) {
      this.search.set('');
    }
    this.page.set(1);
    this.sincronizarFiltroEnUrl(value);
    this.loadUsers();
  }

  onSearchInput(value: string): void {
    this.esperandoBusqueda.set(value.trim() !== this.search().trim());
    this.search$.next(value);
  }

  onTablePageChange(event: PersonTablePageChange): void {
    if (event.page === this.page()) {
      return;
    }
    this.page.set(event.page);
    this.loadUsers();
  }


  onMenuAction(event: PersonTableActionEvent): void {
    const { action, row } = event;
    switch (action) {
      case 'reiniciarFaltas':
        this.confirmReiniciarFaltas(row);
        break;
      case 'verPerfil':
        void this.router.navigate(['/perfil', row.id]);
        break;
      case 'editar':
        void this.router.navigate(['/editar-persona', row.id]);
        break;
      case 'eliminar':
        this.confirmEliminar(row);
        break;
      case 'abrirChat':
        this.confirmAbrirChat(row);
        break;
      case 'estudioTerminado':
        this.confirmarDiscipulado(
          row,
          'Estudio terminado',
          'El estudio de esta persona queda marcado como terminado.',
          'estudio-terminado',
          'Estudio terminado.',
        );
        break;
      case 'cancelarEstudio':
        this.confirmarDiscipulado(
          row,
          'Cancelar estudio',
          'Se cancela el estudio de esta persona.',
          'cancelar-estudio',
          'Estudio cancelado.',
        );
        break;
      case 'bautismoRealizado':
        this.confirmarDiscipulado(
          row,
          'Bautismo realizado',
          'Esta persona queda marcada como bautizada.',
          'bautismo-realizado',
          'Bautismo realizado.',
        );
        break;
      case 'cancelarBautismo':
        this.confirmarDiscipulado(
          row,
          'Cancelar bautismo',
          'Se cancela la inscripción al bautismo.',
          'cancelar-bautismo',
          'Bautismo cancelado.',
        );
        break;
      default:
        break;
    }
  }

  private confirmarDiscipulado(
    row: PersonTableRow,
    header: string,
    message: string,
    accion: 'estudio-terminado' | 'cancelar-estudio' | 'bautismo-realizado' | 'cancelar-bautismo',
    detail: string,
  ): void {
    this.confirmation.confirm({
      header,
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Confirmar',
      rejectLabel: 'Cancelar',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.usersService.marcarDiscipulado(row.id, accion).subscribe({
          next: () => {
            this.messages.add({ severity: 'success', summary: 'Listo', detail });
            this.loadUsers();
          },
          error: () => {
            this.messages.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo completar la acción.',
            });
          },
        });
      },
    });
  }

  private confirmReiniciarFaltas(row: PersonTableRow): void {
    this.confirmation.confirm({
      header: 'Reiniciar faltas',
      message: 'Si acepta, el conteo de faltas de esta persona volvera a cero.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Reiniciar',
      rejectLabel: 'Cancelar',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.usersService.reiniciarFaltas(row.id).subscribe({
          next: () => {
            this.messages.add({
              severity: 'success',
              summary: 'Listo',
              detail: 'Faltas reiniciadas.',
            });
            this.loadUsers();
          },
          error: () => {
            this.messages.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudieron reiniciar las faltas.',
            });
          },
        });
      },
    });
  }

  private confirmAbrirChat(row: PersonTableRow): void {
    this.confirmation.confirm({
      header: 'Abrir chat',
      message: 'El chat de esta persona estara disponible en Solicitudes.',
      icon: 'pi pi-comments',
      acceptLabel: 'Ok',
      rejectLabel: 'Cancelar',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.solicitudesService.abrirChat(row.id).subscribe({
          next: () => {
            this.messages.add({
              severity: 'success',
              summary: 'Listo',
              detail: 'Chat disponible en Solicitudes.',
            });
          },
          error: () => {
            this.messages.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo abrir el chat.',
            });
          },
        });
      },
    });
  }

  dejarSinLider(): void {
    const row = this.personaAEliminar();
    if (!row) {
      return;
    }
    this.avisoLiderVisible.set(false);
    this.pedirConfirmacionPapelera(row, 'sin-lider');
  }

  abrirElegirLider(): void {
    this.liderDestino.set(null);
    this.elegirLiderVisible.set(true);
    this.avisoLiderVisible.set(false);
    this.cargarLideres();
  }

  volverAlAviso(): void {
    this.liderDestino.set(null);
    this.avisoLiderVisible.set(true);
    this.elegirLiderVisible.set(false);
  }

  continuarConLider(): void {
    const row = this.personaAEliminar();
    const idLiderDestino = this.liderDestino();
    if (!row || !idLiderDestino) {
      return;
    }
    const nombre =
      this.lideresOpciones().find((opcion) => opcion.value === idLiderDestino)?.label ?? '';
    this.elegirLiderVisible.set(false);
    this.pedirConfirmacionPapelera(row, 'transferir', idLiderDestino, nombre);
  }

  cancelarEliminarLider(): void {
    this.avisoLiderVisible.set(false);
    this.elegirLiderVisible.set(false);
    this.limpiarEliminarLider();
  }

  onAvisoLiderVisibleChange(visible: boolean): void {
    if (!visible) {
      this.avisoLiderVisible.set(false);
      if (!this.elegirLiderVisible()) {
        this.limpiarEliminarLider();
      }
      return;
    }
    this.avisoLiderVisible.set(true);
  }

  onElegirLiderVisibleChange(visible: boolean): void {
    if (!visible) {
      this.elegirLiderVisible.set(false);
      this.liderDestino.set(null);
      if (!this.avisoLiderVisible()) {
        this.limpiarEliminarLider();
      }
      return;
    }
    this.elegirLiderVisible.set(true);
  }

  private confirmEliminar(row: PersonTableRow): void {
    this.personaAEliminar.set(row);
    this.solicitudesService.resumenDiscipulos(row.id).subscribe({
      next: (resumen) => {
        if (this.personaAEliminar()?.id !== row.id) {
          return;
        }
        if (resumen.lider && resumen.total > 0) {
          this.discipulosTotal.set(resumen.total);
          this.avisoLiderVisible.set(true);
          return;
        }
        this.pedirConfirmacionPapelera(row);
      },
      error: (err: HttpErrorResponse) => {
        this.limpiarEliminarLider();
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: this.mensajeError(err, 'No se pudo revisar si tiene discípulos.'),
        });
      },
    });
  }

  private pedirConfirmacionPapelera(
    row: PersonTableRow,
    discipulos?: AccionDiscipulos,
    idLiderDestino?: string,
    nombreLider = '',
  ): void {
    const destino = nombreLider ? ` Sus discípulos pasan a ${nombreLider}.` : '';
    const alMuelle = discipulos === 'sin-lider'
      ? ' Sus discípulos quedan sin líder y pasan al muelle.'
      : '';
    this.confirmation.confirm({
      header: 'Eliminar persona',
      message: `Se va a mover a la papelera a ${row.nombreCompleto}.${destino}${alMuelle} Continuar?`,
      icon: 'pi pi-trash confirm-icon-papelera',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => this.ejecutarEliminar(row, discipulos, idLiderDestino),
      reject: () => this.limpiarEliminarLider(),
    });
  }

  private ejecutarEliminar(
    row: PersonTableRow,
    discipulos?: AccionDiscipulos,
    idLiderDestino?: string,
  ): void {
    const detail =
      discipulos === 'transferir'
        ? 'Discípulos transferidos. Persona movida a la papelera.'
        : discipulos === 'sin-lider'
          ? 'Los discípulos quedaron sin líder. Persona movida a la papelera.'
          : 'Persona movida a la papelera.';
    this.usersService
      .delete(
        row.id,
        false,
        discipulos ? { accion: discipulos, idLiderDestino } : undefined,
      )
      .subscribe({
        next: () => {
          this.limpiarEliminarLider();
          this.messages.add({ severity: 'success', summary: 'Listo', detail });
          this.loadUsers();
        },
        error: (err: HttpErrorResponse) => {
          const message = this.mensajeError(err, 'No se pudo eliminar.');
          if (!discipulos && err.status === 409 && message.toLowerCase().includes('discípulos')) {
            this.personaAEliminar.set(row);
            this.avisoLiderVisible.set(true);
            return;
          }
          this.limpiarEliminarLider();
          this.messages.add({ severity: 'error', summary: 'Error', detail: message });
        },
      });
  }

  private cargarLideres(): void {
    const personaId = this.personaAEliminar()?.id;
    this.cargandoLideres.set(true);
    this.lideresOpciones.set([]);
    this.usersService.getLideres().subscribe((lideres) => {
      if (this.personaAEliminar()?.id !== personaId) {
        return;
      }
      this.lideresOpciones.set(
        lideres
          .filter((lider) => lider.id !== personaId)
          .map((lider) => ({
            label: `${lider.nombre} ${lider.apellido}`.trim(),
            value: lider.id,
          })),
      );
      this.cargandoLideres.set(false);
    });
  }

  private limpiarEliminarLider(): void {
    this.personaAEliminar.set(null);
    this.discipulosTotal.set(0);
    this.liderDestino.set(null);
    this.lideresOpciones.set([]);
    this.cargandoLideres.set(false);
    this.avisoLiderVisible.set(false);
    this.elegirLiderVisible.set(false);
  }

  private mensajeError(err: HttpErrorResponse, fallback: string): string {
    const message = err.error?.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }

  private aplicarFiltroDeUrl(): void {
    const filtro = this.route.snapshot.queryParamMap.get('filtro');
    if (filtro && this.filtroOptions.some((opt) => opt.id === filtro)) {
      this.filtro.set(filtro as CongregacionFiltro);
    }
  }

  private sincronizarFiltroEnUrl(value: string): void {
    const filtro = value && value !== CongregacionFiltro.Ninguno ? value : null;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { filtro },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private loadUsers(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.loadUsersSub?.unsubscribe();

    const filtro = this.filtro();
    const params: {
      page: number;
      limit: number;
      sort: string;
      filtro?: string;
      q?: string;
    } = {
      page: this.page(),
      limit: PAGE_SIZE,
      sort: 'nombre:asc',
    };

    if (filtro) {
      params.filtro = filtro;
    }
    if (filtro === CongregacionFiltro.PorNombre && this.search().trim()) {
      params.q = this.search().trim();
    }

    this.loadUsersSub = this.usersService.list(params).pipe(
      catchError((err) => {
        if (err?.status === 404) {
          return of({
            success: true,
            data: [],
            pagination: { page: this.page(), limit: PAGE_SIZE, total: 0 },
          });
        }
        this.loadError.set(true);
        return of({
          success: false,
          data: [],
          pagination: { page: this.page(), limit: PAGE_SIZE, total: 0 },
        });
      }),
    ).subscribe({
      next: (res) => {
        this.allUsers.set(res.data ?? []);
        this.totalRecords.set(res.pagination?.total ?? 0);
        this.loading.set(false);
      },
    });
  }

  private loadIglesia(): void {
    this.loadIglesiaSub?.unsubscribe();
    this.loadIglesiaSub = this.iglesiasService.getCurrentIglesia().subscribe({
      next: (iglesia) => this.cantCulto.set(iglesia.cantCulto ?? 1),
      error: () => this.cantCulto.set(1),
    });
  }
}
